import { SignableDocument, SignerType } from 'app/models/document-params'

// One shared derivation of a signable document's signing progress, used by the
// Documents-tab icons (C / AA / FM) and the trade-entry AA status filter so
// they can never disagree on what "done" means.
//
// Required-signature steps come from computeRequiredSignatures in
// daml/rightpedal/.../Utils/MasterTrade.daml: the AA has two steps
// (AllOf [Seller, Buyer], then AllOf [Reviewer, Agent]); C and FM have the
// single counterparty step. For the AA, "executed" (steps cleared) is the
// authoritative rule that gates settlement eligibility — NOT merely "the
// Agent signed". The C / FM icons deliberately keep their evidence-based
// green rule (isSignableDocumentSigned, LOS-5960) and consume only the
// 'seller' / 'buyer' partial states from this helper.
export type SigningState = 'none' | 'seller' | 'buyer' | 'both' | 'executed'

// A party counts as signed whether it signed online via DocuSeal (`signatures`)
// or offline via an admin-uploaded document (`adminSignedFor`).
export const hasPartySigned = (doc: SignableDocument, type: SignerType): boolean =>
  (doc.signatures ?? []).some(s => s.signerType === type) ||
  (doc.adminSignedFor ?? []).some(a => a.signerPartyInfo?.signerType === type)

export const getSigningState = (doc?: SignableDocument): SigningState => {
  if (!doc) return 'none'
  // Strict equality on 0: a missing/malformed requiredSignatures must never
  // read as executed — aaDocumentSigned gates settlement eligibility, so
  // "executed" is the most dangerous default. Only an explicit empty steps
  // array counts.
  if (doc.requiredSignatures?.steps?.length === 0) return 'executed'
  const sellerSigned = hasPartySigned(doc, 'Seller')
  const buyerSigned = hasPartySigned(doc, 'Buyer')
  if (sellerSigned && buyerSigned) return 'both'
  if (sellerSigned) return 'seller'
  if (buyerSigned) return 'buyer'
  return 'none'
}
